'use client';

/**
 * Consecutive-401 breaker for the API client — the app's ONLY involuntary
 * sign-out authority.
 *
 * A session tab whose JWT expired used to keep its poll loop running forever —
 * ~3,000 rejected requests over one night (see
 * planning/analyze9/tool-turn-failures-24h/analysis/05-error-level-events.md).
 * This guard suspends polling after a streak of 401s and recovers the token.
 *
 * It is now a genuine last resort rather than the app's de-facto refresh
 * mechanism: `authTokenFreshness` refreshes ahead of expiry, so reaching this
 * breaker at all means something unusual happened.
 *
 * Three invariants, all learned from the defect this replaces
 * (`planning/auth-session-expiry/plan.md`):
 *
 * - **Only a terminal refresh outcome may sign a user out.** A transient
 *   failure — offline, 429, 5xx, anything unrecognized — keeps the session and
 *   retries. The previous implementation signed out globally on any error at
 *   all, which logged the user out of every device roughly every other night
 *   whenever a laptop woke before its WiFi did.
 * - **Sign-out is LOCAL.** Single-session enforcement is deliberately off for
 *   this project, so multiple devices are a supported state; one tab concluding
 *   its own session is dead must not revoke the user's other devices.
 * - **Only a proven credential recovery retires a recovery run.** An HTTP
 *   response is not that proof: several session routes are public
 *   (`routes.contract.ts` marks `overview`, `initial-load` and `previews`
 *   `PUBLIC_ROUTE_AUTH`), so a 200 on one says nothing about the bearer token.
 *   Recovery is retired by a Supabase `TOKEN_REFRESHED` / `SIGNED_IN` event.
 */

import type {
  AuthChangeEvent,
  Subscription,
  SignOut,
} from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import {
  getLastRefreshAttemptAtMs,
  refreshAccessToken,
} from '@/lib/authTokenRefresh';
import { captureAuthReturnRoute, HOME_ROUTE } from '@/lib/authReturnRoute';
import {
  AuthSessionEvent,
  logAuthSessionEvent,
  logAuthSessionEventAndFlush,
} from '@/lib/authSessionTelemetry';
import { AuthRefreshOutcome } from '@/types/auth-types';
import { LogLevel } from '@cabal/shared/util/seq-log';

const CONSECUTIVE_UNAUTHORIZED_REFRESH_THRESHOLD = 3;

/**
 * A streak only counts while the 401s are actually consecutive in time. The
 * counter used to be a plain tally with no window: because a network error
 * carries no response and therefore neither increments nor resets it, a partial
 * streak could survive an offline gap of hours and combine with an unrelated
 * 401 later.
 */
const UNAUTHORIZED_STREAK_WINDOW_MS = 120_000;

/**
 * No refresh attempt may be made sooner than this after the previous one.
 *
 * `auth-js` caches EVERY failed refresh for `REFRESH_FAILURE_COOLDOWN_MS`
 * (60s), keyed by refresh token, and answers repeat callers from that cache
 * without touching the network (`GoTrueClient.js:4186-4190`, `:4292-4296`).
 * An attempt inside the window therefore reads back its own failure and would
 * be mistaken for independent confirmation, while still costing a ladder step.
 */
const REFRESH_ATTEMPT_FLOOR_MS = 65_000;

/**
 * Delays between recovery attempts, in order; the last value repeats forever.
 *
 * The tail repeats rather than giving up: while recovery is running polling is
 * suspended, so the standing cost is one refresh every five minutes, and the
 * alternative to retrying is signing out a user whose session is fine.
 */
const RECOVERY_DELAYS_MS: readonly number[] = [
  REFRESH_ATTEMPT_FLOOR_MS,
  130_000,
  300_000,
];

const SIGN_OUT_TIMEOUT_MS = 3_000;

const SUPABASE_LOCAL_STORAGE_KEY_PREFIX = 'sb-';

const ONLINE_EVENT: keyof WindowEventMap = 'online';

/** Local scope only — see the module header. */
const LOCAL_SIGN_OUT: SignOut = { scope: 'local' };

/** The only events that prove the bearer credential works again. */
const CREDENTIAL_RECOVERED_EVENTS: readonly AuthChangeEvent[] = [
  'TOKEN_REFRESHED',
  'SIGNED_IN',
];

enum AuthExpiryState {
  Healthy = 'healthy',
  /** Polling suspended while the recovery ladder runs. Session still alive. */
  Recovering = 'recovering',
  /** Terminal: the refresh token is dead and the page is on its way home. */
  Expired = 'expired',
}

enum AuthRecoveryTrigger {
  UnauthorizedStreak = 'unauthorized_streak',
  AuthInitFailure = 'auth_init_failure',
}

let consecutiveUnauthorizedCount = 0;
let lastUnauthorizedAtMs = 0;
let state = AuthExpiryState.Healthy;
let recoverySubscription: Subscription | null = null;

/**
 * Bumped whenever a recovery run is superseded — by a later trip, or by a
 * proven credential recovery. A run whose generation is stale must not change
 * state, so a late `Terminal` result from an abandoned ladder can never sign
 * out a user who has already recovered.
 */
let recoveryGeneration = 0;

/** Poll loops check this each tick; suspended while recovering or expired. */
export function isPollingSuspendedByAuth(): boolean {
  return state !== AuthExpiryState.Healthy;
}

/**
 * Any response that got past the transport resets the consecutive-401 streak.
 *
 * Deliberately does NOT retire a running recovery: public routes answer 200
 * without a valid bearer token, so this is not proof of anything about the
 * credential. `watchForCredentialRecovery` owns that transition.
 */
export function reportApiAuthSuccess(): void {
  consecutiveUnauthorizedCount = 0;
  lastUnauthorizedAtMs = 0;
}

/**
 * Report a 401 on a request that CARRIED a credential.
 *
 * Callers must not report a 401 from a request that went out without an
 * `Authorization` header: it says nothing about the credential, and counting it
 * would let a signed-out visitor's ordinary traffic trip the breaker.
 */
export function reportApiUnauthorized(input: {
  method?: string;
  url?: string;
}): void {
  const now = Date.now();
  if (now - lastUnauthorizedAtMs > UNAUTHORIZED_STREAK_WINDOW_MS) {
    consecutiveUnauthorizedCount = 0;
  }
  lastUnauthorizedAtMs = now;
  consecutiveUnauthorizedCount += 1;

  // Only the head of a streak is logged. The backend records no 401s at all, so
  // without this a sign-out cannot be reconstructed afterwards; per-401 logging
  // would reproduce the 3,000-events-a-night problem this guard was built for.
  // The endpoint is carried because "a credentialed 401 streak caused this" is
  // not an actionable answer without knowing which route started it.
  if (consecutiveUnauthorizedCount === 1) {
    logAuthSessionEvent({
      event: AuthSessionEvent.UnauthorizedObserved,
      props: {
        streakPosition: consecutiveUnauthorizedCount,
        method: input.method ?? null,
        url: input.url ?? null,
      },
      level: LogLevel.Warning,
    });
  }

  if (consecutiveUnauthorizedCount < CONSECUTIVE_UNAUTHORIZED_REFRESH_THRESHOLD) {
    return;
  }

  startRecovery(AuthRecoveryTrigger.UnauthorizedStreak);
}

/**
 * A refresh performed outside the breaker came back terminal. Routed here so
 * that session death has exactly one owner no matter who discovered it — the
 * freshness precondition can observe a dead refresh token long before any 401
 * does, and on a public surface that 401 may never arrive.
 */
export function reportTerminalRefreshOutcome(reason: string | null): void {
  recoveryGeneration += 1;
  void forceSignOut(reason);
}

/**
 * Auth initialization could not settle a session because a refresh failed.
 *
 * `auth-js` reports `{ session: null, error }` when the stored access token has
 * expired and the refresh did not go through — including for a plain network
 * failure, where it deliberately leaves the session in storage
 * (`GoTrueClient.js:2554-2575`, `:4267-4285`). Recovering here is what turns
 * "opened the app on a flaky connection" back into a signed-in session instead
 * of a signed-out one.
 */
export function beginAuthRecovery(): void {
  startRecovery(AuthRecoveryTrigger.AuthInitFailure);
}

function startRecovery(trigger: AuthRecoveryTrigger): void {
  if (state !== AuthExpiryState.Healthy) return;

  state = AuthExpiryState.Recovering;
  recoveryGeneration += 1;
  watchForCredentialRecovery();
  logAuthSessionEvent({
    event: AuthSessionEvent.TokenRefreshForced,
    props: { trigger, consecutiveUnauthorizedCount },
    level: LogLevel.Warning,
  });
  void runRecovery(recoveryGeneration);
}

function retireRecovery(): void {
  if (state !== AuthExpiryState.Recovering) return;
  recoveryGeneration += 1;
  consecutiveUnauthorizedCount = 0;
  lastUnauthorizedAtMs = 0;
  state = AuthExpiryState.Healthy;
  stopWatchingForCredentialRecovery();
}

function watchForCredentialRecovery(): void {
  if (recoverySubscription) return;
  const { data } = supabase.auth.onAuthStateChange((event: AuthChangeEvent) => {
    if (!CREDENTIAL_RECOVERED_EVENTS.includes(event)) return;
    retireRecovery();
  });
  recoverySubscription = data.subscription;
}

function stopWatchingForCredentialRecovery(): void {
  recoverySubscription?.unsubscribe();
  recoverySubscription = null;
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Earliest moment at which another refresh attempt can reach the network.
 *
 * Keyed off the shared attempt clock in `authTokenRefresh`, not a ladder-local
 * one: a preemptive refresh or the `getSession()` refresh at auth init can have
 * populated `auth-js`'s failure cache moments before recovery starts, and an
 * attempt made inside that window would only read that failure back.
 */
function getEarliestAttemptAtMs(): number {
  return getLastRefreshAttemptAtMs() + REFRESH_ATTEMPT_FLOOR_MS;
}

/** Sleep out whatever remains of the floor before touching the network. */
function waitForAttemptFloor(): Promise<void> {
  const remainingMs = getEarliestAttemptAtMs() - Date.now();
  if (remainingMs <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    setTimeout(resolve, remainingMs);
  });
}

/**
 * Resolve after `delayMs`, or as soon as the browser reports it is back online
 * — whichever comes first, but never before the refresh floor has elapsed. A
 * laptop waking up should recover in the second it reconnects, yet an `online`
 * event inside the failure-cooldown window would only buy a cached failure.
 */
function waitForRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      setTimeout(resolve, delayMs);
      return;
    }

    let settled = false;
    let deadlineMs = Date.now() + delayMs;
    let timeoutId = setTimeout(finish, delayMs);

    function finish(): void {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      window.removeEventListener(ONLINE_EVENT, onOnline);
      resolve();
    }

    function onOnline(): void {
      // Only ever bring the deadline forward, and never past the floor.
      const candidateMs = Math.max(getEarliestAttemptAtMs(), Date.now());
      if (candidateMs >= deadlineMs) return;
      deadlineMs = candidateMs;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(finish, Math.max(0, candidateMs - Date.now()));
    }

    window.addEventListener(ONLINE_EVENT, onOnline);
  });
}

/**
 * Never spend an attempt while the browser knows it is offline — the request
 * would fail for a reason that says nothing about the refresh token, and each
 * failed attempt costs a ladder step.
 */
function waitUntilOnline(): Promise<void> {
  if (!isOffline() || typeof window === 'undefined') {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const onOnline = (): void => {
      window.removeEventListener(ONLINE_EVENT, onOnline);
      resolve();
    };
    window.addEventListener(ONLINE_EVENT, onOnline);
  });
}

function getRecoveryDelayMs(attemptIndex: number): number {
  const lastIndex = RECOVERY_DELAYS_MS.length - 1;
  return RECOVERY_DELAYS_MS[Math.min(attemptIndex, lastIndex)];
}

async function runRecovery(generation: number): Promise<void> {
  let attemptIndex = 0;

  while (generation === recoveryGeneration) {
    await waitUntilOnline();
    if (generation !== recoveryGeneration) return;

    await waitForAttemptFloor();
    if (generation !== recoveryGeneration) return;

    const result = await refreshAccessToken();
    if (generation !== recoveryGeneration) return;

    if (result.outcome === AuthRefreshOutcome.Refreshed) {
      retireRecovery();
      return;
    }

    if (result.outcome === AuthRefreshOutcome.NoSession) {
      // Nothing to recover and nothing died — the app is already signed out.
      // Signing out again would send a visitor who never had a session through
      // the forced-sign-out flow.
      retireRecovery();
      return;
    }

    if (result.outcome === AuthRefreshOutcome.Terminal) {
      await forceSignOut(result.reason);
      return;
    }

    await waitForRetry(getRecoveryDelayMs(attemptIndex));
    attemptIndex += 1;
  }
}

function purgeSupabaseStorage(): void {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(SUPABASE_LOCAL_STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // Storage may be unavailable; the navigation below still lands the user on
    // a signed-out home page.
  }
}

async function signOutLocally(): Promise<void> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    // Bounded: the redirect must not wait on an unreachable auth server, but a
    // sign-out that is merely slow should still complete before the page goes.
    await Promise.race([
      supabase.auth.signOut(LOCAL_SIGN_OUT),
      new Promise<void>((resolve) => {
        timeoutId = setTimeout(resolve, SIGN_OUT_TIMEOUT_MS);
      }),
    ]);
  } catch {
    // Falls through to the storage purge.
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }

  // Belt and braces: a sign-out that timed out or threw must not leave a dead
  // session in storage for the next page load to resurrect.
  purgeSupabaseStorage();
}

/**
 * The refresh token is definitively dead. Remember where the user was, sign out
 * locally, and land on the home page — which opens the auth modal and returns
 * them here once they authenticate (`authReturnRoute`).
 */
async function forceSignOut(reason: string | null): Promise<void> {
  if (state === AuthExpiryState.Expired) return;

  state = AuthExpiryState.Expired;
  stopWatchingForCredentialRecovery();
  // Flushed synchronously: the `location.assign` below can cancel an ordinary
  // buffered send, and this event is the only record of why the user was
  // signed out.
  logAuthSessionEventAndFlush({
    event: AuthSessionEvent.SessionSignedOutForced,
    props: { reason },
    level: LogLevel.Warning,
  });

  captureAuthReturnRoute();
  await signOutLocally();

  if (typeof window !== 'undefined') {
    window.location.assign(HOME_ROUTE);
  }
}
